import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  PlusIcon, 
  MapPinIcon, 
  ClockIcon, 
  ChartBarIcon,
  CalendarDaysIcon,
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { formatSpeed, formatDistance, formatFuel, formatDuration } from '../../utils/unitConversions';
import { getTripRunTimeMinutes } from '../../utils/tripTrackStats';
import { formatCalendarDate } from '../../utils/dateUtils';

const TripsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: ${theme.colors.background.primary};
  min-height: 100vh;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-left: auto;
`;

const CreateTripButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${theme.colors.primary.main};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  box-shadow: ${theme.shadows.sm};
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.secondary};
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const StatSubtext = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const SeasonBanner = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const SeasonBannerTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SeasonBannerMeta = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const SeasonBannerLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const RecoverBanner = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: ${theme.borderRadius.lg};
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.45);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const RecoverBannerText = styled.div`
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  line-height: 1.45;

  strong {
    display: block;
    margin-bottom: 0.25rem;
  }
`;

const RecoverButton = styled.button`
  padding: 0.65rem 1.25rem;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background: ${theme.colors.primary.main};
  color: white;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TripsSection = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TripsList = styled.div`
  padding: 1.5rem;
`;

const TripCardContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TripCard = styled(Link)`
  display: flex;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const HoverActions = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  
  ${TripCardContainer}:hover & {
    opacity: 1;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const HoverActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.secondary};
  box-shadow: ${theme.shadows.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  
  &:hover {
    background: ${theme.colors.primary.main};
    color: white;
    transform: scale(1.02);
  }
  
  &.delete {
    background: ${theme.colors.error.light};
    color: ${theme.colors.error.dark};
  }
  
  &.delete:hover {
    background: ${theme.colors.error.main};
    color: white;
  }
`;

const TimeGroupSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimeGroupTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
`;

const TimeGroupIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.color || theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
`;

const TripImage = styled.div`
  width: 200px;
  height: 150px;
  background: ${theme.colors.background.secondary};
  background-image: url(${props => props.src || ''});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.secondary};
  font-size: 2rem;
  flex-shrink: 0;
  
  ${props => !props.src && `
    &::after {
      content: '📸';
      opacity: 0.3;
    }
  `}
  
  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
    height: 160px;
  }
`;

const TripContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const TripHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const TripTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const TripStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: #dcfce7;
          color: #166534;
        `;
      case 'completed':
        return `
          background: #e0f2fe;
          color: #0c4a6e;
        `;
      case 'paused':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const TripDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const TripDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const TripActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.primary.main};
  background: transparent;
  color: ${theme.colors.primary.main};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.primary.main};
    color: white;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TripsPage = () => {
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();
  const [trips, setTrips] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTrip, setHoveredTrip] = useState(null);
  const [deletingTrip, setDeletingTrip] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [stats, setStats] = useState({
    totalTrips: 0,
    totalRunHours: 0,
    totalDistance: 0,
    totalFuelUsed: 0
  });
  const [seasonContext, setSeasonContext] = useState({ seasons: [], label: '' });
  const [missedRecover, setMissedRecover] = useState(null);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    loadTripsData();
  }, []);

  const [loadError, setLoadError] = useState(null);

  const formatSeasonDate = (dateString) => formatCalendarDate(dateString);

  const visibleSeasons = (seasonContext.seasons || []).filter((season) => {
    if (season.status === 'active') return true;
    return trips.some((trip) => trip.vessel_id === season.vessel_id);
  });

  const buildSeasonLabel = (seasons) => {
    const list = seasons?.length ? seasons : visibleSeasons;
    if (!list?.length) return 'Current season';
    if (list.length === 1) {
      const s = list[0];
      return `${s.name || `${s.year} Season`}${s.vessel_name ? ` · ${s.vessel_name}` : ''}`;
    }
    return `${list.length} vessel seasons`;
  };

  /** Collapse duplicate *active* trips with the same name on the same vessel. */
  const dedupeActiveTrips = (tripList) => {
    const byKey = new Map();
    const score = (t) => (
      (t.has_gps_track ? 100000 : 0)
      + (parseFloat(t.total_distance_km || 0) * 10)
      + (new Date(t.start_time || 0).getTime() / 1e10)
    );
    for (const trip of tripList) {
      const key = `${trip.vessel_id}:${String(trip.trip_name || '').trim().toLowerCase()}`;
      const existing = byKey.get(key);
      if (!existing || score(trip) > score(existing)) {
        byKey.set(key, trip);
      }
    }
    return Array.from(byKey.values());
  };

  const loadTripsData = async () => {
    setLoading(true);
    setLoadError(null);
    let seasonTrips = [];
    let activeTripsData = [];
    let summary = null;
    let seasons = [];

    try {
      const response = await api.get('/api/trips/current-season', { timeout: 30000 });
      seasonTrips = response.data.trips || [];
      seasons = response.data.seasons || [];
      summary = response.data.summary || {};
      activeTripsData = dedupeActiveTrips(response.data.active_trips || []);
      setTrips(seasonTrips);
      setActiveTrips(activeTripsData);
      setSeasonContext({
        seasons,
        label: buildSeasonLabel(seasons),
      });
      if (response.data.degraded) {
        toast('Showing trips without full season context', { icon: 'ℹ️' });
      }
      // Don't block first paint on SignalK missed-trip scan
      checkMissedTripRecovery(activeTripsData, seasons).catch((err) => {
        console.warn('Missed trip recovery check failed:', err?.message || err);
      });
    } catch (error) {
      console.error('Error loading current-season trips:', error);
      try {
        const fallback = await api.get('/api/trips', { timeout: 30000 });
        const allTrips = fallback.data?.trips || fallback.data || [];
        const year = new Date().getFullYear();
        seasonTrips = allTrips.filter((trip) => {
          if (!trip.start_time) return true;
          return new Date(trip.start_time).getFullYear() === year;
        });
        if (seasonTrips.length === 0) seasonTrips = allTrips;
        activeTripsData = dedupeActiveTrips(
          allTrips.filter((trip) => trip.trip_status === 'active')
        );
        summary = {
          total_trips: seasonTrips.length,
          total_run_hours: seasonTrips.reduce(
            (sum, trip) => sum + (getTripRunTimeMinutes(trip) || 0) / 60,
            0
          ),
          total_distance_km: seasonTrips.reduce(
            (sum, trip) => sum + (trip.total_distance_km || 0),
            0
          ),
          total_fuel_used_liters: seasonTrips.reduce(
            (sum, trip) => sum + (trip.total_fuel_used_liters || 0),
            0
          ),
        };
        setTrips(seasonTrips);
        setActiveTrips(activeTripsData);
        setSeasonContext({ seasons: [], label: `${year} Season` });
        toast('Loaded trips (season summary unavailable)', { icon: 'ℹ️' });
        checkMissedTripRecovery(activeTripsData, []).catch((err) => {
          console.warn('Missed trip recovery check failed:', err?.message || err);
        });
      } catch (fallbackError) {
        const msg = error.response?.data?.error || error.message || 'Failed to load trips';
        setLoadError(`${msg} — try refreshing in a moment.`);
        toast.error('Could not load season trips');
        setTrips([]);
        setActiveTrips([]);
      }
    }

    setStats({
      totalTrips: summary?.total_trips ?? seasonTrips.length,
      totalRunHours: summary?.total_run_hours ?? summary?.total_engine_hours ?? 0,
      totalDistance: summary?.total_distance_km ?? seasonTrips.reduce((sum, trip) => sum + (trip.total_distance_km || 0), 0),
      totalFuelUsed: summary?.total_fuel_used_liters ?? seasonTrips.reduce((sum, trip) => sum + (trip.total_fuel_used_liters || 0), 0),
    });

    setLoading(false);
  };

  const checkMissedTripRecovery = async (activeTripsList, seasons) => {
    if (activeTripsList.length > 0) {
      setMissedRecover(null);
      return;
    }
    const vesselIds = [...new Set(
      (seasons || []).map((s) => s.vessel_id).filter(Boolean)
    )];
    if (!vesselIds.length) vesselIds.push(1);

    for (const vesselId of vesselIds) {
      try {
        const res = await api.get(`/api/vessels/${vesselId}/missed-trip-status?lookback_hours=72`);
        if (res.data?.can_recover) {
          setMissedRecover({ vesselId, ...res.data });
          return;
        }
      } catch (err) {
        console.warn('missed-trip-status check failed:', err?.message);
      }
    }
    setMissedRecover(null);
  };

  const handleRecoverMissedTrip = async () => {
    if (!missedRecover?.vessel_id) return;
    setRecovering(true);
    try {
      const res = await api.post(`/api/vessels/${missedRecover.vessel_id}/recover-missed-trip`, {
        lookback_hours: 72,
      });
      const tripId = res.data?.trip?.id;
      toast.success(
        `Recovered trip with ${res.data?.tracks_inserted || 0} track points from SignalK`
      );
      setMissedRecover(null);
      await loadTripsData();
      if (tripId) navigate(`/trips/${tripId}/map`);
    } catch (err) {
      const msg =
        err.response?.data?.detail
        || err.response?.data?.error
        || err.response?.data?.reason
        || err.message;
      toast.error(`Could not recover trip: ${msg}`);
    } finally {
      setRecovering(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not started';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <PlayIcon className="w-3 h-3" />;
      case 'paused':
        return <PauseIcon className="w-3 h-3" />;
      case 'completed':
        return <StopIcon className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const groupTripsByTime = (trips) => {
    const now = new Date();
    const groups = {
      today: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };

    trips.forEach(trip => {
      const tripDate = new Date(trip.start_time);
      const daysDiff = Math.floor((now - tripDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === 0) {
        groups.today.push(trip);
      } else if (daysDiff <= 7) {
        groups.thisWeek.push(trip);
      } else if (daysDiff <= 30) {
        groups.thisMonth.push(trip);
      } else {
        groups.older.push(trip);
      }
    });

    return groups;
  };

  const handleDeleteClick = (trip) => {
    setTripToDelete(trip);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tripToDelete) return;
    
    try {
      setDeletingTrip(tripToDelete.id);
      await api.delete(`/api/trips/${tripToDelete.id}`, { timeout: 300000 });
      toast.success('Trip deleted successfully');
      loadTripsData();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error(error.response?.data?.error || 'Failed to delete trip');
    } finally {
      setDeletingTrip(null);
      setShowDeleteModal(false);
      setTripToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTripToDelete(null);
  };

  // Delete Confirmation Modal
  const DeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: theme.colors.background.paper,
          borderRadius: theme.borderRadius.lg,
          padding: '2rem',
          maxWidth: '400px',
          width: '90%',
          boxShadow: theme.shadows.xl
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: theme.colors.text.primary,
            margin: '0 0 1rem 0'
          }}>
            Delete Trip
          </h3>
          <p style={{
            color: theme.colors.text.secondary,
            margin: '0 0 2rem 0',
            lineHeight: '1.5'
          }}>
            Are you sure you want to delete "{tripToDelete?.trip_name}"? This action cannot be undone.
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleDeleteCancel}
              style={{
                padding: '0.75rem 1.5rem',
                border: `1px solid ${theme.colors.border.primary}`,
                background: 'transparent',
                color: theme.colors.text.secondary,
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deletingTrip === tripToDelete?.id}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: theme.colors.error.main,
                color: 'white',
                borderRadius: theme.borderRadius.md,
                cursor: deletingTrip === tripToDelete?.id ? 'not-allowed' : 'pointer',
                opacity: deletingTrip === tripToDelete?.id ? 0.6 : 1
              }}
            >
              {deletingTrip === tripToDelete?.id ? 'Deleting...' : 'Delete Trip'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <TripsContainer>
        <LoadingState>
          <div>Loading your trips...</div>
        </LoadingState>
      </TripsContainer>
    );
  }

  return (
    <TripsContainer>
      <Header>
        <Title>My Trips</Title>
        <HeaderActions>
          <CreateTripButton to="/trips/create">
            <PlusIcon className="w-5 h-5" />
            New Trip
          </CreateTripButton>
        </HeaderActions>
      </Header>

      {loadError && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem 1.25rem',
          borderRadius: theme.borderRadius.md,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          color: theme.colors.text.primary,
        }}>
          <strong>Could not load trips</strong>
          <div style={{ marginTop: '0.35rem', fontSize: '0.9rem' }}>{loadError}</div>
          <button
            type="button"
            onClick={loadTripsData}
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 1rem',
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.border.primary}`,
              background: theme.colors.background.secondary,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {missedRecover?.can_recover && (
        <RecoverBanner>
          <RecoverBannerText>
            <strong>Today&apos;s trip wasn&apos;t auto-created — your boat data is still in SignalK.</strong>
            Departure detected around{' '}
            {new Date(missedRecover.departure_time).toLocaleString()}
            {missedRecover.home_port ? ` from ${missedRecover.home_port}` : ''}.
            Recover GPS route and engine telemetry from stored history.
          </RecoverBannerText>
          <RecoverButton type="button" onClick={handleRecoverMissedTrip} disabled={recovering}>
            {recovering ? 'Salvaging…' : 'Recover today\'s trip'}
          </RecoverButton>
        </RecoverBanner>
      )}

      {visibleSeasons.length > 0 && (
        <SeasonBanner>
          <div>
            <SeasonBannerTitle>
              <CalendarDaysIcon className="w-5 h-5" />
              {buildSeasonLabel(visibleSeasons)}
            </SeasonBannerTitle>
            <SeasonBannerMeta>
              {visibleSeasons.map((season) => (
                <span key={`${season.vessel_id}-${season.id || season.year}`} style={{ display: 'block', marginTop: '0.25rem' }}>
                  {season.vessel_name ? `${season.vessel_name}: ` : ''}
                  {formatSeasonDate(season.start_date)} – {formatSeasonDate(season.end_date)}
                  {season.status === 'inferred'
                    ? ' (calendar year — no active season set)'
                    : season.status === 'completed' || season.status === 'closed'
                      ? ' (last season with trips)'
                      : ''}
                </span>
              ))}
            </SeasonBannerMeta>
          </div>
          {visibleSeasons.length === 1 && visibleSeasons[0].id && (
            <SeasonBannerLink to={`/vessels/${visibleSeasons[0].vessel_id}/seasons/${visibleSeasons[0].id}`}>
              View season report →
            </SeasonBannerLink>
          )}
        </SeasonBanner>
      )}

      {/* Stats Overview */}
      <StatsGrid>
        <StatCard>
          <StatTitle>Total Trips</StatTitle>
          <StatValue>{stats.totalTrips}</StatValue>
          <StatSubtext>This season</StatSubtext>
        </StatCard>
        <StatCard>
          <StatTitle>Run Time</StatTitle>
          <StatValue>{stats.totalRunHours.toFixed(1)}</StatValue>
          <StatSubtext>Hours underway this season</StatSubtext>
        </StatCard>
        <StatCard>
          <StatTitle>Total Distance</StatTitle>
          <StatValue>
            {preferences ? formatDistance(stats.totalDistance / 1.852, preferences.distance_unit, 0) : `${stats.totalDistance} km`}
          </StatValue>
          <StatSubtext>
            {preferences ? (preferences.distance_unit === 'kilometers' ? 'km this season' : preferences.distance_unit === 'statute_miles' ? 'mi this season' : 'nm this season') : 'km this season'}
          </StatSubtext>
        </StatCard>
        <StatCard>
          <StatTitle>Fuel Used</StatTitle>
          <StatValue>
            {preferences ? formatFuel(stats.totalFuelUsed, preferences.fuel_units, 0) : `${stats.totalFuelUsed} L`}
          </StatValue>
          <StatSubtext>
            {preferences ? (preferences.fuel_units === 'gallons' ? 'gal this season' : preferences.fuel_units === 'imperial_gallons' ? 'imp gal this season' : 'L this season') : 'L this season'}
          </StatSubtext>
        </StatCard>
      </StatsGrid>

      {/* Active Trips */}
      {activeTrips.length > 0 && (
        <TripsSection>
          <SectionHeader>
            <SectionTitle>
              <PlayIcon className="w-5 h-5 text-green-500" />
              Active Trips
            </SectionTitle>
          </SectionHeader>
          <TripsList>
            {activeTrips.map((trip) => (
              <TripCardContainer key={trip.id}>
                <TripCard to={`/trips/${trip.id}`}>
                  <TripImage src={trip.primary_image_url} />
                  <TripContent>
                    <div>
                      <TripHeader>
                        <TripTitle>{trip.trip_name}</TripTitle>
                        <TripStatus status={trip.trip_status}>
                          {getStatusIcon(trip.trip_status)}
                          {trip.trip_status}
                        </TripStatus>
                      </TripHeader>
                      <TripDetails>
                        <TripDetail>
                          <MapPinIcon className="w-4 h-4" />
                          {trip.vessel_name}
                        </TripDetail>
                        <TripDetail>
                          <CalendarDaysIcon className="w-4 h-4" />
                          Started {formatDate(trip.start_time)}
                        </TripDetail>
                        <TripDetail>
                          <ClockIcon className="w-4 h-4" />
                          {formatDuration(getTripRunTimeMinutes(trip))}
                        </TripDetail>
                      <TripDetail>
                        <ChartBarIcon className="w-4 h-4" />
                        {preferences ? formatDistance((trip.total_distance_km || 0) / 1.852, preferences.distance_unit, 1) : `${trip.total_distance_km || 0} km`}
                      </TripDetail>
                      </TripDetails>
                    </div>
                    <TripActions>
                      <ActionButton as={Link} to={`/trips/${trip.id}`}>
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        View Details
                      </ActionButton>
                      <ActionButton 
                        as={Link} 
                        to={`/trips/${trip.id}/map`}
                        style={{ backgroundColor: theme.colors.secondary.main, color: 'white' }}
                      >
                        <MapPinIcon className="w-4 h-4" />
                        View Map
                      </ActionButton>
                      {trip.trip_status === 'active' && (
                        <ActionButton
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              await api.put(`/api/trips/${trip.id}`, { trip_status: 'completed' });
                              toast.success('Trip completed');
                              loadTripsData();
                            } catch (err) {
                              console.error('Complete trip failed:', err);
                              toast.error(err.response?.data?.error || 'Failed to complete trip');
                            }
                          }}
                          style={{ backgroundColor: theme.colors.primary.main, color: 'white' }}
                        >
                          <StopIcon className="w-4 h-4" />
                          Complete
                        </ActionButton>
                      )}
                    </TripActions>
                  </TripContent>
                </TripCard>
                <HoverActions>
                  <HoverActionButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/trips/${trip.id}`);
                    }}
                    title="View Details"
                  >
                    View
                  </HoverActionButton>
                  <HoverActionButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/trips/${trip.id}/edit`);
                    }}
                    title="Edit Trip"
                  >
                    Edit
                  </HoverActionButton>
                  <HoverActionButton
                    className="delete"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClick(trip);
                    }}
                    title="Delete Trip"
                  >
                    Delete
                  </HoverActionButton>
                </HoverActions>
              </TripCardContainer>
            ))}
          </TripsList>
        </TripsSection>
      )}

      {/* Time Grouped Trips */}
      {(() => {
        // Active trips have their own section — don't duplicate in time groups
        const nonActiveTrips = trips.filter((t) => t.trip_status !== 'active');
        const groupedTrips = groupTripsByTime(nonActiveTrips);
        const timeGroups = [
          { key: 'today', title: 'Today', icon: 'T', color: '#10b981' },
          { key: 'thisWeek', title: 'This Week', icon: 'W', color: '#3b82f6' },
          { key: 'thisMonth', title: 'This Month', icon: 'M', color: '#8b5cf6' },
          { key: 'older', title: 'Older', icon: 'O', color: '#6b7280' }
        ];

        return timeGroups.map(group => {
          const groupTrips = groupedTrips[group.key];
          if (groupTrips.length === 0) return null;

          return (
            <TimeGroupSection key={group.key}>
              <TimeGroupTitle>
                <TimeGroupIcon color={group.color}>
                  {group.icon}
                </TimeGroupIcon>
                {group.title} ({groupTrips.length})
              </TimeGroupTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {groupTrips.map((trip) => (
                  <TripCardContainer key={trip.id}>
                    <TripCard to={`/trips/${trip.id}`}>
                      <TripImage src={trip.primary_image_url} />
                      <TripContent>
                        <div>
                          <TripHeader>
                            <TripTitle>{trip.trip_name}</TripTitle>
                            <TripStatus status={trip.trip_status}>
                              {getStatusIcon(trip.trip_status)}
                              {trip.trip_status}
                            </TripStatus>
                          </TripHeader>
                          <TripDetails>
                            <TripDetail>
                              <MapPinIcon className="w-4 h-4" />
                              {trip.vessel_name}
                            </TripDetail>
                            <TripDetail>
                              <CalendarDaysIcon className="w-4 h-4" />
                              {formatDate(trip.start_time)} - {formatDate(trip.end_time)}
                            </TripDetail>
                            <TripDetail>
                              <ClockIcon className="w-4 h-4" />
                              {formatDuration(getTripRunTimeMinutes(trip))}
                            </TripDetail>
                      <TripDetail>
                        <ChartBarIcon className="w-4 h-4" />
                        {preferences ? formatDistance((trip.total_distance_km || 0) / 1.852, preferences.distance_unit, 1) : `${trip.total_distance_km || 0} km`}
                      </TripDetail>
                          </TripDetails>
                        </div>
                        <TripActions>
                          <ActionButton as={Link} to={`/trips/${trip.id}`}>
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                            View Details
                          </ActionButton>
                          <ActionButton 
                            as={Link} 
                            to={`/trips/${trip.id}/map`}
                            style={{ backgroundColor: theme.colors.secondary.main, color: 'white' }}
                          >
                            <MapPinIcon className="w-4 h-4" />
                            View Map
                          </ActionButton>
                        </TripActions>
                      </TripContent>
                    </TripCard>
                    <HoverActions>
                      <HoverActionButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/trips/${trip.id}`);
                        }}
                        title="View Details"
                      >
                        View
                      </HoverActionButton>
                      <HoverActionButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/trips/${trip.id}/edit`);
                        }}
                        title="Edit Trip"
                      >
                        Edit
                      </HoverActionButton>
                      <HoverActionButton
                        className="delete"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(trip);
                        }}
                        title="Delete Trip"
                      >
                        Delete
                      </HoverActionButton>
                    </HoverActions>
                  </TripCardContainer>
                ))}
              </div>
            </TimeGroupSection>
          );
        });
      })()}

      {/* Empty State */}
      {trips.length === 0 && !loadError && (
        <EmptyState>
          <EmptyStateIcon>
            <MapPinIcon className="w-8 h-8" />
          </EmptyStateIcon>
          <h3>No trips this season</h3>
          <p>Trips during {seasonContext.label || 'your active season'} show here, with season totals above.</p>
          <CreateTripButton to="/trips/create" style={{ marginTop: '1rem' }}>
            <PlusIcon className="w-5 h-5" />
            Create Your First Trip
          </CreateTripButton>
        </EmptyState>
      )}

      <DeleteModal />
    </TripsContainer>
  );
};

export default TripsPage;
